import type {
  IGamAdUnit,
  IGamCreativeSize,
  IUseGamAdUnitsResult,
  IUseGamCreativeSizesResult,
} from '@/Interfaces/Forecaster/GamInventory.interface';
import { getGamCreativeSizes, getGamSubAdUnits, getGamTopAdUnits } from '@/store/api/gamInventory.api';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

export const useGamTopAdUnits = (enabled: boolean): IUseGamAdUnitsResult => {
  const [adUnits, setAdUnits] = useState<IGamAdUnit[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setAdUnits([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);

    getGamTopAdUnits()
      .then(data => {
        if (!cancelled) setAdUnits(data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        Sentry.captureException(err);
        setAdUnits([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { adUnits, isLoading };
};

export const useGamSubAdUnits = (topAdUnitId: string | null): IUseGamAdUnitsResult => {
  const [adUnits, setAdUnits] = useState<IGamAdUnit[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!topAdUnitId) {
      setAdUnits([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    setAdUnits([]);
    setIsLoading(true);

    getGamSubAdUnits(topAdUnitId)
      .then(data => {
        if (!cancelled) setAdUnits(data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        Sentry.captureException(err);
        setAdUnits([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [topAdUnitId]);

  return { adUnits, isLoading };
};

/** Video creative sizes — network-level, fetched once when the GAM account is present. */
export const useGamCreativeSizes = (enabled: boolean): IUseGamCreativeSizesResult => {
  const [creativeSizes, setCreativeSizes] = useState<IGamCreativeSize[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setCreativeSizes([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);

    getGamCreativeSizes()
      .then(data => {
        if (!cancelled) setCreativeSizes(data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        Sentry.captureException(err);
        setCreativeSizes([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { creativeSizes, isLoading };
};
